// ===================================
// ADMIN LOGIN PAGE
// src/pages/AdminLogin.js
// Separate login page for administrators
// ===================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AdminLogin.css';

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login({ email: formData.email, password: formData.password });

    if (result.success) {
      // Check if user is admin after login
      // The AuthContext will have the user data
      // If not admin, redirect to regular dashboard
      navigate('/admin/dashboard');
    } else {
      setError(result.error || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login-page">
      {/* Animated Background */}
      <div className="admin-bg">
        <div className="admin-orb admin-orb-1"></div>
        <div className="admin-orb admin-orb-2"></div>
      </div>

      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <Link to="/" className="admin-login-logo">
              <div className="admin-logo-icon">⚙️</div>
              <div className="admin-logo-text">
                <span className="admin-brand">Career-Coach.AI</span>
                <span className="admin-label">Admin Portal</span>
              </div>
            </Link>
            <h1>Administrator Login</h1>
            <p>Access the admin dashboard</p>
          </div>

          <form className="admin-login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="admin-alert admin-alert-error">
                <span>⚠️</span>
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="admin@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="admin-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In to Admin
                  <span>→</span>
                </>
              )}
            </button>
          </form>

          <div className="admin-login-footer">
            <p>Not an administrator?</p>
            <Link to="/login">Regular Sign In</Link>
          </div>

          <div className="admin-back-home">
            <Link to="/">← Back to Home</Link>
          </div>
        </div>

        <div className="admin-info-section">
          <h2>Admin Dashboard</h2>
          <ul className="admin-features-list">
            <li>
              <span className="feature-icon">👥</span>
              <div>
                <strong>User Management</strong>
                <p>View, edit, and manage all users</p>
              </div>
            </li>
            <li>
              <span className="feature-icon">📊</span>
              <div>
                <strong>Analytics Dashboard</strong>
                <p>Platform metrics and insights</p>
              </div>
            </li>
            <li>
              <span className="feature-icon">💰</span>
              <div>
                <strong>Revenue Tracking</strong>
                <p>Monitor payments and revenue</p>
              </div>
            </li>
            <li>
              <span className="feature-icon">⚙️</span>
              <div>
                <strong>Platform Settings</strong>
                <p>Configure pricing and options</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
